import {
  analyse,
  bestSplit,
  comparablePrice,
  effectivePrice,
  fmtNum,
  lei,
  lineCost,
  pendingAlts,
  splitCost,
  splitError,
  transportText,
  type Rfq,
  type SplitInput,
  t,
} from '@procumi/shared';
import { useState } from 'react';
import type { Toast } from '../hooks/useToast';
import { api, errMsg } from '../lib/api';
import { Dialog } from './Dialog';

/**
 * Comanda împărțită (cerută de proprietar, 2026-10-16): fiecare poziție de la
 * furnizorul ales pentru ea. Propunerea e cel mai mic preț pe poziție, ca în
 * tabelul comparativ; omul o poate schimba poziție cu poziție. Din alegere iese
 * câte o comandă pentru fiecare furnizor, fiecare cu transportul ei.
 */
export function SplitDialog({
  rfq,
  toast,
  onUpdated,
  onClose,
}: {
  rfq: Rfq;
  toast: Toast;
  onUpdated: (r: Rfq) => void;
  onClose: () => void;
}) {
  const offers = rfq.sends.filter((s) => s.offer);
  const [pick, setPick] = useState<Record<string, string>>(() => {
    const from = rfq.chosen?.split?.length
      ? rfq.chosen.split.map((p) => ({ supplierId: p.supplier.id, itemIds: p.itemIds }))
      : (bestSplit(rfq) ?? []);
    const out: Record<string, string> = {};
    for (const p of from) for (const id of p.itemIds) out[id] = p.supplierId;
    // ce n-a primit nimic (o poziție fără ofertă): cel mai ieftin care a cotat-o, altfel primul
    for (const it of rfq.items) {
      if (out[it.id]) continue;
      const priced = offers
        .map((s) => ({ s, p: effectivePrice(s.offer!, it) }))
        .filter((x): x is { s: (typeof offers)[number]; p: number } => x.p != null)
        .sort((a, b) => a.p - b.p);
      out[it.id] = (priced[0]?.s ?? offers[0] ?? rfq.sends[0])?.supplier.id ?? '';
    }
    return out;
  });
  const [note, setNote] = useState(rfq.chosen?.note ?? '');
  const [close, setClose] = useState(!rfq.closedAt && !rfq.canceledAt);
  const [busy, setBusy] = useState(false);

  // Părțile, în ordinea trimiterilor: fiecare furnizor cu pozițiile lui.
  const parts: SplitInput[] = rfq.sends
    .map((s) => ({
      supplierId: s.supplier.id,
      itemIds: rfq.items.filter((it) => pick[it.id] === s.supplier.id).map((it) => it.id),
    }))
    .filter((p) => p.itemIds.length);
  const cost = splitCost(rfq, parts);
  const error = splitError(rfq, parts);
  const a = analyse(rfq);
  const bestWhole = a.full.length ? a.full.reduce((x, y) => (y.total < x.total ? y : x)) : null;
  const nameOf = (id: string) => rfq.sends.find((s) => s.supplier.id === id)?.supplier.name ?? '';
  // Ticket 10: împărțirea acceptă echivalentele nedecise de pe pozițiile luate de la fiecare furnizor.
  const withAlts = parts
    .filter((p) => {
      const offer = rfq.sends.find((s) => s.supplier.id === p.supplierId)?.offer;
      return (
        offer &&
        pendingAlts(
          offer,
          rfq.items.filter((it) => p.itemIds.includes(it.id)),
        ).length
      );
    })
    .map((p) => nameOf(p.supplierId));

  const save = async () => {
    if (error) return toast(error);
    setBusy(true);
    try {
      onUpdated(
        await api.chooseSupplier(rfq.id, {
          supplierId: parts[0]!.supplierId,
          note: note.trim(),
          total: null,
          close,
          split: parts,
        }),
      );
      toast(t('Comanda e împărțită între {0} furnizori', parts.length));
      onClose();
    } catch (e) {
      toast(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog onClose={onClose} wide>
      <div className="dlg-h">
        <div>
          <h2>{t('Împarte comanda')}</h2>
          <p className="hint">
            {t(
              'Fiecare poziție de la furnizorul ales pentru ea. Am propus cel mai mic preț pe poziție; schimbă unde ai alt motiv. Iese câte o comandă pentru fiecare furnizor, fiecare cu transportul ei.',
            )}
          </p>
        </div>
      </div>

      <div className="dlg-b pad">
        <div>
          <table className="data-table split-table">
            <thead>
              <tr>
                <th>{t('Poziție')}</th>
                <th className="num">{t('Cantitate')}</th>
                <th>{t('De la')}</th>
                <th className="num">{t('Valoare')}</th>
              </tr>
            </thead>
            <tbody>
              {rfq.items.map((it) => {
                const send = rfq.sends.find((s) => s.supplier.id === pick[it.id]);
                const value = send?.offer ? lineCost(send.offer, it) : null;
                return (
                  <tr key={it.id}>
                    <td data-label={t('Poziție')}>{it.desc}</td>
                    <td data-label={t('Cantitate')} className="num">
                      {fmtNum(it.qty)} {it.unit}
                    </td>
                    <td data-label={t('De la')}>
                      <select
                        className="inp"
                        value={pick[it.id] ?? ''}
                        aria-label={t('Furnizorul pentru {0}', it.desc)}
                        onChange={(e) => setPick({ ...pick, [it.id]: e.target.value })}
                      >
                        {rfq.sends.map((s) => {
                          const p = s.offer ? comparablePrice(s.offer, it) : null;
                          return (
                            <option key={s.id} value={s.supplier.id}>
                              {s.supplier.name}
                              {p == null ? t(' · necotat') : t(' · {0} lei/{1}', fmtNum(p), it.unit)}
                            </option>
                          );
                        })}
                      </select>
                    </td>
                    <td data-label={t('Valoare')} className="num">
                      {value == null ? <span className="muted">{t('fără preț')}</span> : lei(value)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <ul className="split-sum">
          {cost.parts.map((c) => {
            const offer = rfq.sends.find((s) => s.supplier.id === c.supplierId)?.offer;
            const n = parts.find((p) => p.supplierId === c.supplierId)!.itemIds.length;
            return (
              <li key={c.supplierId}>
                <span>
                  <b>{nameOf(c.supplierId)}</b>
                  <small>
                    {n === 1 ? t('1 poziție') : t('{0} poziții', n)}
                    {offer ? t(' · transport {0}', transportText(offer)) : ''}
                    {c.unpriced ? t(' · {0} fără preț', c.unpriced) : ''}
                  </small>
                </span>
                <b className="num">{lei(c.total)}</b>
              </li>
            );
          })}
          <li className="split-total">
            <span>
              <b>{t('Total împărțit')}</b>
              {cost.total != null && bestWhole ? (
                <small>
                  {cost.total < bestWhole.total - 1
                    ? t(
                        '−{0} față de cea mai bună ofertă întreagă ({1})',
                        lei(bestWhole.total - cost.total),
                        bestWhole.supplier.name,
                      )
                    : cost.total > bestWhole.total + 1
                      ? t(
                          '+{0} peste cea mai bună ofertă întreagă ({1})',
                          lei(cost.total - bestWhole.total),
                          bestWhole.supplier.name,
                        )
                      : t('cât cea mai bună ofertă întreagă ({0})', bestWhole.supplier.name)}
                </small>
              ) : cost.total == null ? (
                <small>{t('o poziție n-are preț de la furnizorul ales pentru ea')}</small>
              ) : null}
            </span>
            <b className="num">{cost.total == null ? '—' : lei(cost.total)}</b>
          </li>
        </ul>

        {withAlts.length ? (
          <p className="read-warn">
            {t('Accepți și produsele echivalente propuse de {0}.', withAlts.join(' și '))}
          </p>
        ) : null}

        {error ? (
          <p className="read-warn" role="alert">
            {error}
          </p>
        ) : null}

        <label className="f" style={{ marginTop: 'var(--space-4)' }}>
          {t('Motivul')}
          <input
            className="inp"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={t('plasa e mai ieftină la celălalt furnizor')}
            maxLength={500}
          />
          <span className="hint">{t('Opțional. Un rând ajunge.')}</span>
        </label>

        {!rfq.closedAt ? (
          <label className="primary-pick" style={{ marginTop: 'var(--space-3)' }}>
            <input type="checkbox" checked={close} onChange={(e) => setClose(e.target.checked)} />
            {t('Închide și cererea')}
          </label>
        ) : null}
      </div>

      <div className="dlg-f">
        <button className="btn" onClick={onClose}>
          {t('Anulează')}
        </button>
        <button className="btn primary" disabled={busy || !!error} onClick={() => void save()}>
          {busy ? t('Se salvează…') : t('Împarte comanda')}
        </button>
      </div>
    </Dialog>
  );
}
